import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Minus, Plus, Undo2, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { slotWhere } from '../../../utils/nurseryRowSlots';
import { sharePick, type PickGroup } from '../../../utils/pickCustomers';
import Button from '../../ui/Button';
import nurseryService from '../../../services/nursery';
import { errorMessage } from '../../../utils/apiResponse';
import { cn } from '../../../utils/cn';
import { TagSwatch } from '../TagSwatch';
import LotBands from '../LotBands';
import PickItemCard from './PickItemCard';
import type { LotBand } from '../../../types/nurseryColour';
import { SHORT_REASON_LABELS, type PickItem, type PickList, type PickShortReason } from '../../../types/nursery';

/**
 * The same lot on several of one customer's orders (two purchase orders from
 * one store), as one card: the picker takes the trees once. What they pick is
 * recorded against each order, oldest order first, so each order is still
 * invoiced on its own.
 *
 * Taking the rest of a short from another lot is done per order: "Each order
 * separately" opens the ordinary cards for that.
 */

export interface CombinedPickCardProps {
  group: PickGroup;
  loadId: number;
  despatchFarmName: string;
  timezone: string;
  editable: boolean;
  onChanged: (list: PickList) => void;
  highlighted?: boolean;
  bands?: LotBand[];
}

const REASONS = Object.keys(SHORT_REASON_LABELS) as PickShortReason[];

const inputCls = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

export function CombinedPickCard({ group, loadId, despatchFarmName, timezone, editable, onChanged, highlighted, bands }: CombinedPickCardProps) {
  const { t } = useTranslation('nursery', { keyPrefix: 'CombinedPickCard' });
  const first = group.items[0];
  const [mode, setMode] = useState<'idle' | 'short'>('idle');
  const [separately, setSeparately] = useState(false);
  const [qty, setQty] = useState(group.quantity);
  const [reason, setReason] = useState<PickShortReason | ''>('');
  const [notes, setNotes] = useState('');
  const [fixCount, setFixCount] = useState(false);
  const [counted, setCounted] = useState<number | ''>('');
  const [readyDate, setReadyDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unpicked = group.items.filter((it) => !it.pick);
  const unpickedQty = unpicked.reduce((s, it) => s + it.quantity, 0);
  const anyPicked = group.items.some((it) => it.pick);
  const anyShort = group.items.some((it) => it.pick?.status === 'short');

  /** One call per order, in turn; the last list back is the page's. Stops at the first refusal. */
  const each = async (calls: (() => Promise<{ success: boolean; data?: PickList } & object>)[], fallback: string) => {
    setBusy(true);
    setError(null);
    let last: PickList | null = null;
    try {
      for (const call of calls) {
        const res = await call();
        if (!res.success || !res.data) {
          setError(errorMessage(res, fallback));
          return false;
        }
        last = res.data;
      }
      return true;
    } catch (e) {
      setError(errorMessage(e, fallback));
      return false;
    } finally {
      if (last) onChanged(last);
      setBusy(false);
    }
  };

  const pickAll = () => each(
    unpicked.map((it) => () => nurseryService.recordPick(it.allocation_id, { quantity: it.quantity })),
    t('pickFailed'),
  );

  const undoAll = () => each(
    group.items.filter((it) => it.pick).map((it) => () => nurseryService.undoPick(it.allocation_id)),
    t('undoFailed'),
  );

  const saveShort = async () => {
    if (qty < group.quantity && !reason) {
      setError(t('chooseReason'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (fixCount && counted !== '') {
        // What is left in the row after this pick, plus what this save takes off the lot.
        await nurseryService.correctLotCount(first.lot_id, {
          counted: Number(counted) + (qty - group.picked), new_ready_date: readyDate || null,
          reason: reason ? SHORT_REASON_LABELS[reason] : null, load_id: loadId,
        });
      } else if (reason === 'not_ready' && readyDate) {
        await nurseryService.correctLotCount(first.lot_id, {
          counted: first.lot_count, new_ready_date: readyDate, reason: SHORT_REASON_LABELS.not_ready, load_id: loadId,
        });
      }
    } catch (e) {
      setBusy(false);
      setError(errorMessage(e, t('correctFailed')));
      return;
    }
    setBusy(false);
    const ok = await each(
      sharePick(group.items, qty).map(({ item, quantity }) => () => nurseryService.recordPick(item.allocation_id, {
        quantity, short_reason: quantity < item.quantity ? (reason || null) : null, notes: notes || null,
      })),
      t('pickFailed'),
    );
    if (ok) setMode('idle');
  };

  const openForm = () => {
    setQty(anyPicked ? group.picked : group.quantity);
    const shortPick = group.items.find((it) => it.pick?.status === 'short')?.pick;
    setReason(shortPick?.short_reason ?? '');
    setNotes(group.items.find((it) => it.pick?.notes)?.pick?.notes ?? '');
    setMode('short');
  };

  const orderLine = (it: PickItem) => {
    const state = !it.pick ? null
      : it.pick.status === 'picked' ? t('orderPicked', { n: it.pick.quantity_picked })
        : t('orderShort', { picked: it.pick.quantity_picked, total: it.quantity });
    return [t('orderQty', { order: it.order_number, n: it.quantity }), state].filter(Boolean).join(' · ');
  };

  return (
    <li id={`pick-group-${group.key}`} className={cn('rounded-xl border bg-white p-4', highlighted && 'ring-2 ring-primary-500',
      group.pickedInFull ? 'border-green-200 bg-green-50/40'
        : anyShort ? 'border-amber-200 bg-amber-50/40'
          : !first.at_despatch_farm ? 'border-neutral-200 bg-neutral-50' : 'border-neutral-200')}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-neutral-900 break-words">
            {first.cultivar_name}{first.rootstock_name ? ` / ${first.rootstock_name}` : ''}
          </p>
          <p className="text-sm text-neutral-600 break-words">{first.tree_type_name}{first.lot_code ? ` · ${t('lotCode', { code: first.lot_code })}` : ''}</p>
          {bands && bands.length > 0 && <LotBands bands={bands} className="mt-0.5" />}
          {first.sold_as && (
            <p className="mt-0.5 flex items-center gap-1.5 text-xs text-neutral-600 break-words">
              <TagSwatch hex={first.tag_colour_hex} name={first.tag_colour_name} />
              <span>
                {t('soldAs', { name: first.sold_as })}
                {first.tag_colour_name ? ` · ${t('tag', { colour: first.tag_colour_name })}` : ''}
              </span>
            </p>
          )}
          <p className="text-sm text-neutral-800 mt-1 break-words">{slotWhere(first)}</p>
          <p className="mt-1 inline-block rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-800 max-w-full break-words">
            {t('combined', { count: group.items.length })}
          </p>
          <ul className="mt-1 space-y-1" aria-label={t('orders')}>
            {group.items.map((it) => (
              <li key={it.allocation_id} className="text-xs text-neutral-600 break-words">
                {orderLine(it)}
                {it.line_notes && (
                  <span className="mt-0.5 block rounded bg-blue-50 border border-blue-200 px-2 py-1 text-sm text-blue-900 break-words">
                    {it.line_notes}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-2xl font-bold text-neutral-900 leading-none">{group.quantity}</p>
          <p className="text-xs text-neutral-500">{t('toPick')}</p>
        </div>
      </div>

      {!first.at_despatch_farm && (
        <p className="mt-3 flex items-start gap-2 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>{t('notHere', { at: first.farm_name ?? t('noKnownFarm'), farm: despatchFarmName })}</span>
        </p>
      )}

      {anyPicked && mode === 'idle' && (
        <p className={cn('mt-3 text-sm font-medium', group.pickedInFull ? 'text-green-800' : 'text-amber-800')}>
          {group.pickedInFull
            ? <span className="inline-flex items-center gap-1.5"><Check className="h-4 w-4" /> {t('picked', { n: group.picked })}</span>
            : group.resolved ? t('short', { picked: group.picked, total: group.quantity })
              : t('soFar', { picked: group.picked, total: group.quantity })}
        </p>
      )}

      {error && <p className="mt-3 text-sm text-red-700 break-words">{error}</p>}

      {editable && first.at_despatch_farm && mode === 'idle' && !separately && (
        <div className="mt-3 flex flex-wrap gap-2">
          {unpicked.length > 0 && (
            <Button size="md" onClick={pickAll} disabled={busy} className="flex-1 min-w-[10rem] justify-center">
              <Check className="h-5 w-5" /> {t('pickedAll', { n: unpickedQty })}
            </Button>
          )}
          <Button variant="outline" size={anyPicked ? 'sm' : 'md'} disabled={busy} onClick={openForm}>
            {anyPicked ? t('change') : t('fewer')}
          </Button>
          {anyPicked && (
            <Button variant="outline" size="sm" disabled={busy} onClick={undoAll}>
              <Undo2 className="h-4 w-4" /> {t('undo')}
            </Button>
          )}
        </div>
      )}

      {editable && mode === 'idle' && (
        <div className="mt-2">
          <Button variant="ghost" size="sm" onClick={() => setSeparately(!separately)} aria-expanded={separately}>
            {separately ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            {anyShort && !separately ? t('separatelyShort') : t('separately')}
          </Button>
        </div>
      )}

      {separately && (
        <ul className="mt-2 space-y-3">
          {group.items.map((it) => (
            <PickItemCard
              key={it.allocation_id}
              item={it}
              loadId={loadId}
              despatchFarmName={despatchFarmName}
              timezone={timezone}
              editable={editable}
              onChanged={onChanged}
              bands={bands}
            />
          ))}
        </ul>
      )}

      {mode === 'short' && (
        <div className="mt-4 space-y-3 border-t border-neutral-100 pt-3">
          <div>
            <span className="block text-sm font-medium text-neutral-700 mb-1">{t('howMany')}</span>
            <div className="flex items-center gap-2">
              <button type="button" aria-label={t('oneFewer')} onClick={() => setQty((q) => Math.max(0, q - 1))}
                className="h-10 w-10 flex items-center justify-center rounded-lg border border-neutral-300"><Minus className="h-4 w-4" /></button>
              <input type="number" inputMode="numeric" min={0} max={group.quantity} value={qty} aria-label={t('quantityPicked')}
                onChange={(e) => setQty(Math.max(0, Math.min(group.quantity, Number(e.target.value) || 0)))}
                className="w-20 border border-neutral-300 rounded-lg px-2 py-2 text-center text-lg font-semibold" />
              <button type="button" aria-label={t('oneMore')} onClick={() => setQty((q) => Math.min(group.quantity, q + 1))}
                className="h-10 w-10 flex items-center justify-center rounded-lg border border-neutral-300"><Plus className="h-4 w-4" /></button>
              <span className="text-sm text-neutral-500">{t('of', { n: group.quantity })}</span>
            </div>
            {qty < group.quantity && (
              <p className="mt-1 text-xs text-neutral-600 break-words">{t('oldestFirst')}</p>
            )}
          </div>

          {qty < group.quantity && (
            <fieldset>
              <legend className="block text-sm font-medium text-neutral-700 mb-1">{t('why')}</legend>
              <div className="space-y-1">
                {REASONS.map((r) => (
                  <label key={r} className="flex items-center gap-2 text-sm text-neutral-700">
                    <input type="radio" name={`reason-g-${first.allocation_id}`} checked={reason === r} onChange={() => setReason(r)} />
                    {t(`shortReason.${r}`, { defaultValue: SHORT_REASON_LABELS[r] })}
                  </label>
                ))}
              </div>
            </fieldset>
          )}

          {reason === 'not_ready' && !fixCount && (
            <div>
              <label htmlFor={`ready-g-${first.allocation_id}`} className="block text-sm font-medium text-neutral-700 mb-1">
                {t('whenReady')}
              </label>
              <input id={`ready-g-${first.allocation_id}`} type="date" value={readyDate} onChange={(e) => setReadyDate(e.target.value)} className={inputCls} />
            </div>
          )}

          <label className="flex items-start gap-2 text-sm text-neutral-700">
            <input type="checkbox" className="mt-0.5" checked={fixCount} onChange={(e) => setFixCount(e.target.checked)} />
            <span>{anyPicked ? t('countWrong', { n: first.lot_count }) : t('countWrongBefore', { n: first.lot_count })}</span>
          </label>
          {fixCount && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor={`count-g-${first.allocation_id}`} className="block text-sm font-medium text-neutral-700 mb-1">
                  {t('leftInRow')}
                </label>
                <input id={`count-g-${first.allocation_id}`} type="number" inputMode="numeric" min={0} value={counted}
                  onChange={(e) => setCounted(e.target.value === '' ? '' : Math.max(0, Number(e.target.value)))} className={inputCls} />
              </div>
              <div>
                <label htmlFor={`ready2-g-${first.allocation_id}`} className="block text-sm font-medium text-neutral-700 mb-1">
                  {t('newReadyDate')}
                </label>
                <input id={`ready2-g-${first.allocation_id}`} type="date" value={readyDate} onChange={(e) => setReadyDate(e.target.value)} className={inputCls} />
              </div>
            </div>
          )}

          <div>
            <label htmlFor={`notes-g-${first.allocation_id}`} className="block text-sm font-medium text-neutral-700 mb-1">{t('notes')}</label>
            <input id={`notes-g-${first.allocation_id}`} value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} />
          </div>

          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => { setMode('idle'); setError(null); }} disabled={busy}>{t('cancel')}</Button>
            <Button size="sm" onClick={saveShort} disabled={busy}>{busy ? t('saving') : t('recordPicked', { n: qty })}</Button>
          </div>
        </div>
      )}
    </li>
  );
}

export default CombinedPickCard;
